import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { hasModule } from "@/lib/modules";
import { NURSE_SECTIONS } from "./NurseExtras";
import { ModuleLocked } from "./ModuleGate";
import { LayoutDashboard, Users, GraduationCap, ScanLine, FileBarChart2, LogOut, Settings, ShieldCheck, Building2, Wallet, HeartPulse, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Check, Pencil, GripVertical, Video, Music, ClipboardList, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import Logo from "./Logo";
import PageTransition from "./PageTransition";
import ThemedBackground from "./ThemedBackground";
import ChatWidget from "./ChatWidget";
import SiteFooter from "./SiteFooter";
import PickupAlerts from "./PickupAlerts";
import PushToggle from "./PushToggle";
import FabDock from "@/components/app/FabDock";
import NotificationCenter from "./NotificationCenter";
import PushPrompt from "./PushPrompt";
import { RenewBar } from "./SubscriptionUI";
import { applyTheme } from "@/lib/themes";
import LogoSplash from "./LogoSplash";

function Brand({ user }) {
  if (user?.logo_url) {
    return (
      <div className="flex items-center gap-2.5 min-w-0" data-testid="brand-logo">
        <img src={`${API}${user.logo_url}`} alt="" className="h-8 sm:h-9 w-auto max-w-[120px] sm:max-w-[150px] object-contain shrink-0" />
        {user?.logo_show_name && user?.school_name && (
          <span className="font-display font-bold text-navy text-base sm:text-lg truncate">{user.school_name}</span>
        )}
      </div>
    );
  }
  if (user?.school_name) {
    return (
      <div className="flex items-center gap-2.5 min-w-0" data-testid="brand-name">
        <div className="w-9 h-9 rounded-lg bg-navy text-white flex items-center justify-center font-display font-bold text-lg shrink-0">
          {user.school_name.trim().charAt(0).toUpperCase()}
        </div>
        <span className="font-display font-bold text-navy text-base sm:text-lg truncate">{user.school_name}</span>
      </div>
    );
  }
  return <Logo />;
}

const NAV = [
  { to: "/dashboard", label: "Información general", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/students", label: "Alumnos", icon: Users, testid: "nav-students" },
  { to: "/teachers", label: "Docentes y Personal", icon: GraduationCap, testid: "nav-teachers" },
  { to: "/cobranza", label: "Cobranza", icon: Wallet, testid: "nav-cobranza", module: "cobranza" },
  { to: "/admisiones", label: "Admisiones", icon: ClipboardList, testid: "nav-admisiones", module: "admisiones" },
  { to: "/enfermeria", label: "Enfermería", icon: HeartPulse, testid: "nav-enfermeria", module: "enfermeria" },
  { to: "/direccion", label: "Dirección", icon: Building2, testid: "nav-direccion" },
  { to: "/comunicacion", label: "Reunión digital", icon: Video, testid: "nav-comunicacion", module: "comunicacion" },
  { to: "/evaluaciones", label: "Evaluaciones", icon: ClipboardList, testid: "nav-evaluaciones", module: "evaluaciones" },
  { to: "/musica", label: "Música", icon: Music, testid: "nav-musica", module: "musica" },
  { to: "/scan", label: "Estación NFC", icon: ScanLine, testid: "nav-scan", module: "asistencia" },
  { to: "/reports", label: "Reportes", icon: FileBarChart2, testid: "nav-reports", module: "asistencia" },
  { to: "/settings", label: "Ajustes", icon: Settings, testid: "nav-settings" },
];
const OWNER_NAV = { to: "/admin", label: "Acceso", icon: ShieldCheck, testid: "nav-admin" };

// Ordena los items del menú según el orden guardado por el usuario; agrega al final los que falten.
function orderItems(base, savedOrder) {
  if (!Array.isArray(savedOrder) || !savedOrder.length) return base;
  const map = Object.fromEntries(base.map((i) => [i.to, i]));
  const ordered = savedOrder.map((to) => map[to]).filter(Boolean);
  const rest = base.filter((i) => !savedOrder.includes(i.to));
  return [...ordered, ...rest];
}

export default function AppShell({ children }) {
  const { user, logout, checkAuth, setUser } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const routeModule = NAV.find((n) => loc.pathname.startsWith(n.to))?.module;
  const hasChat = hasModule(user, "comunicacion");
  const isStaff = user?.role === "staff";
  const isCoord = isStaff && user?.staff_kind === "coordinacion";
  const isNurse = isStaff && user?.staff_kind === "enfermeria";
  const STAFF_ALLOWED = ["/dashboard", "/students", "/teachers", "/cobranza", "/admisiones", "/enfermeria", "/direccion", "/reports", "/scan", "/comunicacion", "/musica", "/evaluaciones"];
  const COORD_ALLOWED = ["/direccion", "/evaluaciones", "/enfermeria", "/comunicacion", "/musica", "/reports"];
  const NURSE_ALLOWED = ["/enfermeria", "/comunicacion"];
  const baseItems = isNurse
    ? [...NURSE_SECTIONS.map((s) => ({ to: `/enfermeria/${s.key}`, label: s.label, icon: s.icon, testid: `nav-enf-${s.key}`, module: "enfermeria" })), NAV.find((n) => n.to === "/comunicacion")].filter(Boolean)
    : isCoord
    ? COORD_ALLOWED.map((to) => NAV.find((n) => n.to === to)).filter(Boolean).map((n) => n.to === "/direccion" ? { ...n, label: "Portal de coordinación" } : n)
    : (isStaff
      ? NAV.filter((n) => STAFF_ALLOWED.includes(n.to)).map((n) => n.to === "/direccion" ? { ...n, label: "Área administrativa" } : n)
      : NAV);
  const isOwner = user?.role === "owner";

  const [editNav, setEditNav] = useState(false);
  const [order, setOrder] = useState(baseItems);
  const [savingNav, setSavingNav] = useState(false);
  const [unread, setUnread] = useState(0);
  useEffect(() => { setOrder(orderItems(baseItems, user?.nav_order)); /* eslint-disable-next-line */ }, [user?.nav_order, isStaff]);

  useEffect(() => {
    if (!user || !hasChat) return;
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get("/chat/unread"); if (alive) setUnread(data.unread || 0); } catch { /* silencioso */ }
    };
    load();
    const t = setInterval(load, 12000);
    return () => { alive = false; clearInterval(t); };
  }, [user?.user_id]);

  const items = editNav ? order : orderItems(baseItems, user?.nav_order);

  const moveItem = (index, dir) => {
    setOrder((arr) => {
      const next = [...arr];
      const j = index + dir;
      if (j < 0 || j >= next.length) return next;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };
  const saveNavOrder = async () => {
    setSavingNav(true);
    try {
      const nav_order = order.map((i) => i.to);
      await api.put("/auth/profile", { nav_order });
      if (setUser && user) setUser({ ...user, nav_order });
      toast.success("Orden de botones guardado");
      setEditNav(false);
    } catch { toast.error("No se pudo guardar el orden"); }
    finally { setSavingNav(false); }
  };
  const cancelNav = () => { setOrder(orderItems(baseItems, user?.nav_order)); setEditNav(false); };

  useEffect(() => {
    applyTheme(user?.theme_palette, user?.theme_btn, {
      title: user?.theme_title_color, body: user?.theme_text_color, btn: user?.theme_btn_text_color,
    }, user?.theme_logo_colors, user?.theme_panel_color, user?.theme_custom_color);
  }, [user?.theme_palette, user?.theme_btn, user?.theme_title_color, user?.theme_text_color, user?.theme_btn_text_color, user?.theme_logo_colors, user?.theme_panel_color, user?.theme_custom_color]);

  return (
    <div className="min-h-screen bg-nardo-50 flex relative">
      <LogoSplash sessionKey={user?.role === "staff" ? "admin" : "app"} />
      <ThemedBackground bgId={user?.theme_bg} speed={user?.theme_bg_speed} watermarkUrl={user?.watermark_url ? `${API}${user.watermark_url}` : (user?.logo_url ? `${API}${user.logo_url}` : null)} />
      {hasChat && <ChatWidget />}
      <PushToggle floating />
      <NotificationCenter floating />
      <FabDock />
      {hasModule(user, "asistencia") && <PickupAlerts />}
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-nardo-200 bg-white relative z-10 md:sticky md:top-0 md:h-screen md:overflow-y-auto">
        <div className="px-6 py-6 border-b border-nardo-200">
          <Brand user={user} />
        </div>
        <nav className="flex-1 px-3 py-6 flex flex-col gap-1" data-testid="sidebar-nav">
          {editNav ? (
            <>
              <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.2em] text-nardo-400">Reordena con las flechas</div>
              {items.map(({ to, label, icon: Icon, testid }, i) => (
                <div key={to} data-testid={`nav-edit-${testid}`} className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-nardo-300 bg-nardo-50/60">
                  <GripVertical className="w-4 h-4 text-nardo-400" />
                  <Icon className="w-4 h-4 text-navy" strokeWidth={1.75} />
                  <span className="flex-1 text-sm text-navy truncate">{label}</span>
                  <button onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`nav-up-${testid}`} className="p-1 text-nardo-500 hover:text-navy disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} data-testid={`nav-down-${testid}`} className="p-1 text-nardo-500 hover:text-navy disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="flex gap-2 mt-3 px-1">
                <button onClick={saveNavOrder} disabled={savingNav} data-testid="nav-save-order" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-navy hover:bg-navy-light text-white text-xs font-medium py-2 rounded-md disabled:opacity-50"><Check className="w-3.5 h-3.5" /> {savingNav ? "Guardando…" : "Guardar"}</button>
                <button onClick={cancelNav} data-testid="nav-cancel-order" className="px-3 text-xs text-nardo-500 hover:text-navy border border-nardo-200 rounded-md">Cancelar</button>
              </div>
            </>
          ) : (
            items.map(({ to, label, icon: Icon, testid, module }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-nardo-50 text-navy accent-line-left"
                      : "text-nardo-600 hover:text-navy hover:bg-nardo-50"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{label}</span>
                {!hasModule(user, module) && <Lock className="w-3.5 h-3.5 ml-auto text-nardo-400" data-testid={`${testid}-locked`} />}
                {to === "/comunicacion" && unread > 0 && (
                  <span data-testid="nav-chat-badge" className="ml-auto min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">{unread > 99 ? "99+" : unread}</span>
                )}
              </NavLink>
            ))
          )}
          {!isStaff && !editNav && (
            <button onClick={() => setEditNav(true)} data-testid="nav-edit-toggle" className="mt-2 flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded-md transition-colors">
              <Pencil className="w-4 h-4" strokeWidth={1.75} /> <span>Editar lugar de botones</span>
            </button>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-nardo-200">
          <div className="flex items-center gap-3 mb-3">
            {(user?.photo_url || user?.picture) ? (
              <img src={user?.photo_url ? `${API}${user.photo_url}` : user.picture} alt="" className="w-9 h-9 rounded-full border border-nardo-200 object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-nardo-200 flex items-center justify-center text-navy font-display font-bold">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-nardo-500 truncate">{user?.school_name}</div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] text-nardo-600 hover:text-navy border border-nardo-200 hover:border-navy transition-colors rounded-md"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <RenewBar />
        <header className="md:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-nardo-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1 overflow-hidden">
            <Brand user={user} />
          </div>
          {!isStaff && (
            <button onClick={() => setEditNav((v) => !v)} data-testid="nav-edit-toggle-mobile" className={`shrink-0 p-2 rounded-md ${editNav ? "bg-navy text-white" : "text-nardo-600 hover:text-navy"}`} title="Editar lugar de botones">
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <button onClick={logout} data-testid="logout-button-mobile" className="shrink-0 text-xs uppercase tracking-widest text-nardo-600 hover:text-navy">Salir</button>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-10 pb-36 md:pb-10">
          <PushPrompt />
          {isOwner && (
            <div className="mb-6 bg-navy/5 border border-navy/20 rounded-md px-4 py-3 flex items-center justify-between text-sm" data-testid="owner-banner">
              <span className="text-navy">Estás viendo la app como usuario. Tu panel de creador está separado.</span>
              <NavLink to="/admin" className="text-xs uppercase tracking-[0.25em] text-navy hover:text-navy-light border border-navy/30 hover:border-navy px-3 py-1.5 rounded-md" data-testid="back-to-admin">Ir al panel del creador →</NavLink>
            </div>
          )}
          <PageTransition>{hasModule(user, routeModule) ? children : <ModuleLocked moduleKey={routeModule} />}</PageTransition>
          <SiteFooter />
        </main>

        {/* Mobile bottom nav - carrusel horizontal */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-nardo-200 overflow-x-auto no-scrollbar" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)" }} data-testid="mobile-nav">
          {editNav ? (
            <div className="p-2">
              <div className="flex w-max gap-2 pb-2">
                {items.map(({ to, label, icon: Icon, testid }, i) => (
                  <div key={to} data-testid={`nav-edit-${testid}-mobile`} className="flex flex-col items-center gap-1 shrink-0 w-[5.25rem] border border-dashed border-nardo-300 rounded-md py-2 bg-nardo-50/60">
                    <Icon className="w-[18px] h-[18px] text-navy" strokeWidth={1.75} />
                    <span className="text-[9px] uppercase tracking-wide text-navy text-center leading-tight px-0.5">{label}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`nav-left-${testid}`} className="p-1 text-nardo-500 disabled:opacity-30"><ArrowLeft className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} data-testid={`nav-right-${testid}`} className="p-1 text-nardo-500 disabled:opacity-30"><ArrowRight className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={saveNavOrder} disabled={savingNav} data-testid="nav-save-order-mobile" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-navy text-white text-xs font-medium py-2 rounded-md disabled:opacity-50"><Check className="w-3.5 h-3.5" /> {savingNav ? "Guardando…" : "Guardar orden"}</button>
                <button onClick={cancelNav} data-testid="nav-cancel-order-mobile" className="px-4 text-xs text-nardo-500 border border-nardo-200 rounded-md">Cancelar</button>
              </div>
            </div>
          ) : (
          <div className="flex w-max">
            {items.map(({ to, label, icon: Icon, testid, module }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testid}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 px-1 shrink-0 w-[5.25rem] text-center text-[10px] font-medium uppercase tracking-wide leading-tight transition-colors ${
                    isActive ? "text-navy" : "text-nardo-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`relative flex items-center justify-center w-9 h-9 rounded-full transition-colors ${isActive ? "bg-navy text-white" : "text-nardo-500"}`}>
                      <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      {to === "/comunicacion" && unread > 0 && (
                        <span data-testid="nav-chat-badge-mobile" className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none border border-white">{unread > 9 ? "9+" : unread}</span>
                      )}
                    </span>
                    <span className="leading-tight inline-flex items-center gap-0.5">{!hasModule(user, module) && <Lock className="w-2.5 h-2.5" />}{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
          )}
        </nav>
      </div>
    </div>
  );
}
